/**
 * An opt-in trace of every write to the hanging-protocol step atoms and the viewport buckets.
 *
 * This exists because a stack trace cannot answer "who wrote this". Recoil applies a `set` updater
 * lazily inside its own batch, so by the time one of these writers runs, the calling component's
 * frame is gone -- an `Error().stack` captured here contains `mySet`, `selectorSet`, `setNodeValue`
 * and nothing above them. That was measured twice while chasing VX-5091, and both times the trail
 * ended in Recoil internals. So the writers name *themselves* instead, and the intent travels as an
 * argument.
 *
 * Kept in the app deliberately rather than removed with the investigation. Four of the SHP bug
 * families to date (VX-4624, VX-4732, VX-4768, VX-4772, then VX-5091) were "the right value written
 * to the wrong place, or the wrong value written to the right place", and every one of them cost a
 * round of ad-hoc `console.log` in these exact functions. Having the trail already there, named by
 * intent, is what turns that into one run.
 *
 * **Off unless asked for**, because these writers run on every viewport edit and every step
 * navigation. Two switches, and they are independent:
 *
 * ```js
 * // From the browser console, persists across reloads for this origin:
 * window.__SIRONA_HP_TRACE__(true);
 * // Or, before the app boots (what the e2e suite uses):
 * localStorage.setItem('srna.trace.hp', 'true');
 * ```
 *
 * **Writes to the console directly, not through `modules/logger`.** That is a deliberate exception to
 * the house rule, and it is forced: `Logger#log` only reaches the console when remote logging is
 * *off* (`env.TEST !== 'true' && !this.#remoteLogging`), and remote logging is on for every build
 * whose `DEPLOY_ENV` is not `local`. So on a PR preview or on deployed dev a `logger` call goes to
 * Datadog and nowhere a developer or a Playwright console listener can see it -- measured: this
 * module logged through `logger.info` first and captured exactly zero lines on a preview run. A trace
 * whose entire purpose is the browser console cannot use a transport that suppresses the console.
 *
 * `console.debug`, with the `no-console` rule disabled for that one line. The rule allows only `warn`
 * and `error`, and this is neither -- calling it a warning would put a trace line into whatever a
 * reader has warnings wired up to, and it would read as a problem in a Datadog session or a bug
 * report. `debug` is what it is, and the exception is narrower than mislabelling every line.
 *
 * Note `debug` is filtered out of Chrome's console by default: the level selector has to include
 * Verbose. That is the right trade for something off by default and aimed at whoever switched it on.
 */

const TRACE_STORAGE_KEY = 'srna.trace.hp';

/** `null` until first asked, so a disabled trace costs one boolean check per write. */
let isTracingEnabled: boolean | null = null;

function readTracingPreference(): boolean {
  try {
    return globalThis.localStorage?.getItem(TRACE_STORAGE_KEY) === 'true';
  } catch {
    // A storage-denied context (some embeds, some privacy modes) is not a reason to throw from a
    // write path.
    return false;
  }
}

/** Turns the trace on or off for this origin, and remembers it across reloads. */
export function setHPMutationTracing(enabled: boolean): void {
  isTracingEnabled = enabled;
  try {
    if (enabled) globalThis.localStorage?.setItem(TRACE_STORAGE_KEY, 'true');
    else globalThis.localStorage?.removeItem(TRACE_STORAGE_KEY);
  } catch {
    // In-memory only, then. The switch still works for this page.
  }
}

export function isHPMutationTracingEnabled(): boolean {
  isTracingEnabled ??= readTracingPreference();
  return isTracingEnabled;
}

/**
 * Records one write, named by the intent that made it.
 *
 * `detail` is flattened into `key=value` pairs rather than logged as an object, because the browser
 * console elides object contents when a headless run captures it -- an object-shaped log reads as
 * `{bucket: __flat__, nulled: Array(2)}` in Playwright output, which is exactly the part you need.
 * Arrays are joined; anything else goes through `String`.
 */
export function traceHPMutation(intent: string, detail: Record<string, unknown> = {}): void {
  if (!isHPMutationTracingEnabled()) return;
  const pairs = Object.entries(detail).map(([key, value]) => {
    if (Array.isArray(value)) return `${key}=[${value.map(String).join(',')}]`;
    return `${key}=${value == null ? 'null' : String(value)}`;
  });
  // eslint-disable-next-line no-console
  console.debug(`[HP:trace] ${intent}${pairs.length > 0 ? ` ${pairs.join(' ')}` : ''}`);
}

/**
 * Publishes the console switch. Called once from the viewer's entry so the name exists before anyone
 * needs it -- a switch you have to import is a switch nobody finds at 2am.
 */
export function installHPMutationTraceSwitch(): void {
  (globalThis as unknown as Record<string, unknown>).__SIRONA_HP_TRACE__ = (
    enabled: boolean = true
  ) => {
    setHPMutationTracing(enabled);
    return `[HP:trace] ${enabled ? 'on' : 'off'}`;
  };
}
